import React, { useState, useEffect, useRef } from 'react';

const CostCounter = ({ totalCost, onToggle, isVisible, compact = false }) => {
  const [displayCost, setDisplayCost] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const previousCostRef = useRef(0);

  // Smooth cost animation when totalCost changes
  useEffect(() => {
    if (totalCost !== previousCostRef.current) {
      setIsAnimating(true);
      setShowPulse(true);

      // Animate the number change
      const startCost = displayCost;
      const endCost = totalCost;
      const duration = 800;
      const startTime = Date.now();

      const animateNumber = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Easing function for smooth animation
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentCost = startCost + (endCost - startCost) * easeOutCubic;

        setDisplayCost(currentCost);

        if (progress < 1) {
          requestAnimationFrame(animateNumber);
        } else {
          setIsAnimating(false);
          previousCostRef.current = totalCost;
        }
      };

      requestAnimationFrame(animateNumber);

      // Remove pulse effect after animation
      setTimeout(() => setShowPulse(false), 1000);
    }
  }, [totalCost, displayCost]);

  // Format cost for display
  const formatCost = (cost) => {
    if (cost >= 1) {
      return `$${cost.toFixed(2)}`;
    } else if (cost >= 0.001) {
      return `$${cost.toFixed(3)}`;
    } else {
      return `$${cost.toFixed(4)}`;
    }
  };

  // Handle toggle with localStorage persistence
  const handleToggle = () => {
    const newVisibility = !isVisible;
    localStorage.setItem('lrbrain-cost-counter-visible', newVisibility.toString());
    onToggle(newVisibility);
  };

  // Test function - add some cost for testing (temporary)
  const handleTestCost = (e) => {
    e.stopPropagation(); // Prevent toggle when clicking test
    if (window.testCostUpdate) {
      window.testCostUpdate(0.001 + Math.random() * 0.01); // Add random small cost
    }
  };

  if (!isVisible) {
    return (
      <div className={`lrbrain-cost-counter-toggle-button ${compact ? 'compact' : ''}`} onClick={handleToggle}>
        <span className="cost-icon">💰</span>
      </div>
    );
  }

  // Compact variant for header
  if (compact) {
    return (
      <div className={`lrbrain-cost-counter-compact ${isAnimating ? 'animating' : ''}`} onClick={handleToggle}>
        <span className="cost-icon">💰</span>
        <div className="cost-text">
          <div className="cost-label">API Cost</div>
          <div className={`cost-value ${isAnimating ? 'animating' : ''}`}>
            {formatCost(displayCost)}
          </div>
        </div>
      </div>
    );
  }

  // Full variant with orbital rings
  return (
    <div className={`lrbrain-cost-counter ${showPulse ? 'pulse-active' : ''}`}>
      {/* Orbital Rings */}
      <div className="orbital-rings">
        <div className="orbital-ring ring-1"></div>
        <div className="orbital-ring ring-2"></div>
        <div className="orbital-ring ring-3"></div>
      </div>

      {/* Main Counter Display */}
      <div className="cost-display-container" onClick={handleToggle}>
        <div className="cost-icon-container">
          <span className="cost-icon">💰</span>
        </div>
        <div className="cost-text-container">
          <div className="cost-label">API Cost</div>
          <div className={`cost-value ${isAnimating ? 'animating' : ''}`}>
            {formatCost(displayCost)}
          </div>
          {/* Temporary test button - remove in production */}
          <div
            style={{fontSize: '0.6rem', opacity: 0.7, cursor: 'pointer', marginTop: '2px'}}
            onClick={handleTestCost}
            title="Click to test cost animation"
          >
            +test
          </div>
        </div>
      </div>

      {/* Background Glow Effect */}
      <div className="background-glow"></div>

      {/* Energy Pulse Effect */}
      {showPulse && <div className="energy-pulse"></div>}
    </div>
  );
};

export default CostCounter;